import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { cn } from "@/lib/utils";
import { fieldError, fieldHelp, fieldInput, fieldLabel } from "./form-styles";

type FieldShellProps = {
  id: string;
  label: string;
  required?: boolean | undefined;
  error?: string | undefined;
  help?: ReactNode;
  children: ReactNode;
  className?: string;
};

export function FieldShell({
  id,
  label,
  required,
  error,
  help,
  children,
  className,
}: FieldShellProps) {
  const describedBy =
    [help ? `${id}-help` : null, error ? `${id}-error` : null].filter(Boolean).join(" ") ||
    undefined;

  return (
    <div className={cn("min-w-0", className)}>
      <label htmlFor={id} className={fieldLabel}>
        {label}
        {required ? <span className="text-primary"> *</span> : null}
      </label>
      {children}
      {help ? (
        <p id={`${id}-help`} className={fieldHelp}>
          {help}
        </p>
      ) : null}
      {error ? (
        <p id={`${id}-error`} role="alert" className={fieldError}>
          {error}
        </p>
      ) : null}
      {/* expose describedBy for children via clone isn't needed — callers pass aria attrs */}
      <span className="sr-only" data-describedby={describedBy} />
    </div>
  );
}

type TextInputProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: string | undefined;
  help?: ReactNode;
};

export function TextInput({
  id,
  label,
  required,
  error,
  help,
  className,
  ...props
}: TextInputProps) {
  const describedBy =
    [help ? `${id}-help` : null, error ? `${id}-error` : null].filter(Boolean).join(" ") ||
    undefined;

  return (
    <FieldShell id={id!} label={label} required={required} error={error} help={help}>
      <input
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(fieldInput, error && "border-destructive", className)}
        {...props}
      />
    </FieldShell>
  );
}

type TextTextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string;
  error?: string | undefined;
  help?: ReactNode;
};

export function TextTextarea({
  id,
  label,
  required,
  error,
  help,
  className,
  ...props
}: TextTextareaProps) {
  const describedBy =
    [help ? `${id}-help` : null, error ? `${id}-error` : null].filter(Boolean).join(" ") ||
    undefined;

  return (
    <FieldShell id={id!} label={label} required={required} error={error} help={help}>
      <textarea
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(
          fieldInput,
          "min-h-[120px] resize-y",
          error && "border-destructive",
          className,
        )}
        {...props}
      />
    </FieldShell>
  );
}

type RadioListProps = {
  legend: string;
  name: string;
  required?: boolean | undefined;
  error?: string | undefined;
  help?: ReactNode;
  value?: string | undefined;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
};

export function RadioList({
  legend,
  name,
  required,
  error,
  help,
  value,
  options,
  onChange,
}: RadioListProps) {
  const id = name;
  const describedBy =
    [help ? `${id}-help` : null, error ? `${id}-error` : null].filter(Boolean).join(" ") ||
    undefined;

  return (
    <fieldset aria-describedby={describedBy} className="min-w-0">
      <legend className={fieldLabel}>
        {legend}
        {required ? <span className="text-primary"> *</span> : null}
      </legend>
      <div className="mt-3 flex flex-col gap-2.5">
        {options.map((opt) => (
          <label
            key={opt.value}
            className="flex cursor-pointer items-center gap-3 text-sm text-foreground"
          >
            <input
              type="radio"
              name={name}
              value={opt.value}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
              className="size-4 accent-primary"
            />
            {opt.label}
          </label>
        ))}
      </div>
      {help ? (
        <p id={`${id}-help`} className={fieldHelp}>
          {help}
        </p>
      ) : null}
      {error ? (
        <p id={`${id}-error`} role="alert" className={fieldError}>
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}

type TextSelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label: string;
  error?: string | undefined;
  help?: ReactNode;
  placeholder?: string;
  options: Array<{ value: string | number; label: string }>;
};

export function TextSelect({
  id,
  label,
  required,
  error,
  help,
  className,
  placeholder,
  options,
  ...props
}: TextSelectProps) {
  const describedBy =
    [help ? `${id}-help` : null, error ? `${id}-error` : null].filter(Boolean).join(" ") ||
    undefined;

  return (
    <FieldShell id={id!} label={label} required={required} error={error} help={help}>
      <select
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(fieldInput, error && "border-destructive", className)}
        {...props}
      >
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}
